import Form, { FormData, FormDefinition } from "../../../Components/Form/Form";
import { deleteSPFirm, getSPFirmDetailsById, updateSPFirm } from "../../../services/adminService";
import { useNavigate } from "react-router-dom";
import * as T from "../../../utilities/frontendTypes";
import { newServiceProviderFirmFormDefinition } from "../../../utilities/formDefinitionTemplates";
import { useContext, useState } from "react";
import { flattenObject, handleFormSaveResponse } from "../../../utilities/helperFunctions";
import { LoadingContext, LoadingContextType, ToastContext, ToastContextType, useAsync, useReloadOnOutletSave, useRequiredParams } from "../../../utilities/customHooks";
import ContentPageTopButtons from "../../../Components/ContentPageTopButtons";
import { DetailsContentHeader, detailsContentWrapperStyle, formContainerStyle, formWrapperStyle } from "../ProfilePages/NewAdmin";
import LoadingIndicator from "../../../Components/LoadingIndicator";
import ErrorIndicator from "../../../Components/ErrorIndicator";
import { Address } from "../../../Components/Form/AddressFields";
import { basic_number_validator, basic_text_validator, boolean_selection_validator, dash_number_validator } from "../../../utilities/formFieldValidators";
import Button, { ButtonProp } from "../../../Components/Button";
import Modal from "../../../Components/Modal";
import textData from "../../../textData.json"


function FirmDetails() {
  
  const showToast = (useContext(ToastContext) as ToastContextType).showToast;

  const navigate = useNavigate();
  const [formDisabledState, setFormDisabledState] = useState(true);
  const [reloadOnSave, setReloadOnSave] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {showLoading, hideLoading} = useContext(LoadingContext) as LoadingContextType;

  const firmId = useRequiredParams("firmId");
  const firmDetailsAsync = useAsync(() => getSPFirmDetailsById(firmId), [firmId, reloadOnSave]);

  if (firmDetailsAsync.status === "pending") {
    return <LoadingIndicator />;
  }

  if (firmDetailsAsync.status === "rejected") {
    return <ErrorIndicator />;
  }

  const firmDetails: T.IServiceProviderFirm & {_id: T.FirmId} = firmDetailsAsync.value.data;

  const { createdAt, description, websiteURL, name, industriesOfFocus, languages, contact, contactEmail, isActive, address, _id, coordinates, region } = firmDetails;
  const { lng, lat } = coordinates;
  const flattenedAddress = flattenObject(address);

  delete flattenedAddress._id;

  const firmDetailsFormData: FormData = {
    description,
    region,
    name,
    websiteURL,
    industriesOfFocus,
    languages,
    contact,
    contactEmail,
    isActive,
    dateCreated: createdAt,
    lng,
    lat,
    ...flattenedAddress,
  }
  
  // [TODO] add UI <=> DB state converters after Address component is figured out and upgraded with auto complete
  //        once done, combine the two saving functions on FirmDetails and NewFirm
  const handleSave = async (formData: FormData) => {
    if (formData === null) throw new Error("Cannot create service provider firm with null FormData");

    const address: Address = {
      city: formData.city as string,
      streetNumber: formData.streetNumber as string,
      streetName: formData.streetName as string,
      unitNumber: formData.unitNumber as string | null,
      province: formData.province as string,
      postalCode: formData.postalCode as string,
      country: formData.country as string,
    }

    const updatedFirmData = {
      ...formData,
      address,
      firmId: _id,
    }
    
    showLoading();
    const response = await updateSPFirm(updatedFirmData as T.ServiceProviderFirm & { firmId: T.FirmId });
    hideLoading();

    setFormDisabledState(true);
    setReloadOnSave(!reloadOnSave);
    handleFormSaveResponse(
      response, 
      showToast, 
      {message: `Firm '${formData.name}' has been succesfully updated`}, 
      {message: `Firm '${formData.name}'could not be updated, please try again. If you see this message again, please contact support.`}
    );
  }

  const handleDelete = async () => {
    showLoading();
    const response = await deleteSPFirm(_id);
    hideLoading();

    if (response.success) {
      navigate("/dashboard/sp-firms")
    } else {
      setIsModalOpen(false);
    }
    handleFormSaveResponse(
      response, 
      showToast, 
      {message: `Service provider firm '${name}' has been succesfully deleted`}, 
      {message: `Service provider firm '${name}' could not be deleted, please check if it is still active.`}
    );
  }

  const modifiedSPFirmFormDefinition: FormDefinition = {...newServiceProviderFirmFormDefinition};
  delete modifiedSPFirmFormDefinition.address;

  // [TODO] refactor out the addressString approach after Address UI component refactor
  const updateServiceProviderFirmFormDefinition: FormDefinition = {
    ...modifiedSPFirmFormDefinition,
    isActive: {
      label: "Is Firm On Map?",
      inputType: "boolean",
      validator: boolean_selection_validator,
    },
    streetNumber: {
      label: "Street Number",
      inputType: "text",
      validator: basic_text_validator,
    },
    streetName: {
      label: "Street Name",
      inputType: "text",
      validator: basic_text_validator,
    },
    unitNumber: {
      label: "Unit Number",
      inputType: "text",
      validator: {
        ...basic_text_validator,
        required: false
      }
    },
    postalCode: {
      label: "Postal Code",
      inputType: "text",
      validator: basic_text_validator,
    },
    city: {
      label: "City",
      inputType: "text",
      validator: basic_text_validator,
    },
    province: {
      label: "Province",
      inputType: "text",
      validator: basic_text_validator,
    },
    country: {
      label: "Country",
      inputType: "text",
      validator: basic_text_validator,
    },
    lng: {
      label: "Longitude",
      tooltip: {
        content: "If you updated firm address manually, please make sure lng/lat coordinates matches the new address for more accurate marker display on map view.",
        position: "top-left"
      },
      inputType: "number",
      validator: dash_number_validator,
    },
    lat: {
      label: "Latitude",
      tooltip: {
        content: "If you updated firm address manually, please make sure lng/lat coordinates matches the new address for more accurate marker display on map view.",
        position: "top-left"
      },
      inputType: "number",
      validator: dash_number_validator,
    }
  }

  const primaryButtonProp: ButtonProp = {
    buttonText: "Save",
    actionHandler: handleSave,
  }

  const href= `https://${websiteURL}`
  return (
    <div className="resourcesCont w-full !pt-0">
      <h2>{name}</h2>
      <div className="flex flex-col firmDetailsDesc max-w-[578px]">
        <a className="underline bodyLarger mb-2" href={href} target="_blank">{websiteURL}</a>
        <p className="">{description}</p>
      </div>
      <div className="flex w-full firmDetailsCont">
        <div className="flex flex-col firmDetailsContInner justify-between">
          <div>
            <p className="font-semibold bodyLarger mb-1">{textData.Pages.FounderPages.ServiceProviderFirmPages.FirmDetails.Language.eng}</p>
            <p>{languages}</p>
          </div>
          <div className="firmSpacer">
            <p className="font-semibold bodyLarger mb-1">{textData.Pages.FounderPages.ServiceProviderFirmPages.FirmDetails.Focus.eng}</p>
            <p>{industriesOfFocus}</p>
          </div>
        </div>
        <div className="flex flex-col firmDetailsContInner">
          <p className="font-semibold bodyLarger mb-1">{textData.Pages.FounderPages.ServiceProviderFirmPages.FirmDetails.Location.eng}</p>
            {Array.isArray(contact) 
              ? contact.map((item, index) => (
                  <p key={index} className="font-semibold mb-1">{item}</p>
                )) 
              : <p className="font-semibold mb-1">{contact}</p>
            }
            <div className="flex flex-col">
            {Array.isArray(contactEmail) 
              ? contactEmail.map((item, index) => (
                <div className="flex mb-1">
                  <svg xmlns="http://www.w3.org/2000/svg" className="mr-2" width="21" height="15" viewBox="0 0 21 15" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M0.643217 1.48439C2.48353 3.12505 4.23794 4.62706 5.56312 5.74541C6.28 6.34952 6.87069 6.84091 7.28344 7.18229C7.43758 7.31057 7.56688 7.41712 7.66826 7.49987C4.25759 10.3022 3.87174 10.6457 0.780714 13.3954C0.735196 13.4357 0.688645 13.4761 0.64313 13.5174C0.582096 10.6012 0.552096 9.05066 0.552096 7.49985C0.552096 5.94904 0.582183 4.39957 0.643217 1.48439ZM1.09424 13.8546C5.59016 13.9167 7.9508 13.9477 10.3104 13.9477C12.6741 13.9477 15.038 13.9167 19.5482 13.8546C16.3672 10.9095 16.03 10.6115 12.5262 7.85765L11.2186 9.0028C10.6983 9.45798 9.92241 9.45798 9.40206 9.0028L8.09759 7.86178C4.62283 10.7168 4.2587 11.0407 1.1502 13.8047C1.13158 13.8212 1.11286 13.838 1.09424 13.8546ZM8.27766 7.28573C8.28076 7.2878 8.28283 7.28987 8.2849 7.29194L9.76521 8.58709C10.0776 8.86018 10.5431 8.86018 10.8555 8.58709L12.3359 7.29194C12.3379 7.28987 12.341 7.2878 12.3441 7.28573L12.3772 7.25884C12.399 7.24022 12.4321 7.21332 12.4765 7.17712C12.5645 7.1047 12.6969 6.99712 12.8696 6.85435C13.2162 6.56781 13.7283 6.1416 14.3914 5.58195C15.6172 4.54643 17.3603 3.0537 19.5244 1.14509C15.0296 1.08302 12.67 1.05199 10.3104 1.05199C7.94975 1.05199 5.58913 1.08302 1.09136 1.14509C2.89858 2.75368 4.61591 4.22473 5.9191 5.32322C6.63392 5.9263 7.22358 6.41665 7.6353 6.757C7.84012 6.92665 8.0015 7.05906 8.11116 7.14907C8.16495 7.19355 8.20736 7.22872 8.23529 7.25148L8.27766 7.28573ZM19.9775 1.48042C17.77 3.4304 15.9927 4.952 14.7472 6.00414C14.0821 6.56483 13.569 6.99207 13.2214 7.27964C13.119 7.36343 13.0311 7.43584 12.9586 7.49584C16.4106 10.2103 16.7634 10.5249 19.9786 13.501C20.0386 10.5951 20.0686 9.04771 20.0686 7.50008C20.0686 5.94941 20.0385 4.39745 19.9775 1.48042ZM0.375242 0.603188C10.3093 0.465604 10.3114 0.465604 20.2452 0.603188C20.3932 0.605257 20.5142 0.725256 20.5173 0.873176C20.6549 7.49801 20.6549 7.50199 20.5173 14.1268C20.5142 14.2748 20.3932 14.3947 20.2452 14.3968C10.3112 14.5344 10.3091 14.5344 0.375242 14.3968C0.227314 14.3947 0.106286 14.2747 0.103188 14.1268C-0.034396 7.50199 -0.034396 7.49801 0.103188 0.873176C0.106291 0.725248 0.227324 0.605254 0.375242 0.603188Z" fill="#1A2E25"/></svg>
                  <p key={index}>{item}</p>
                </div>
                )) 
              : 
              <div className="flex">
                <svg xmlns="http://www.w3.org/2000/svg" className="mr-2" width="21" height="15" viewBox="0 0 21 15" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M0.643217 1.48439C2.48353 3.12505 4.23794 4.62706 5.56312 5.74541C6.28 6.34952 6.87069 6.84091 7.28344 7.18229C7.43758 7.31057 7.56688 7.41712 7.66826 7.49987C4.25759 10.3022 3.87174 10.6457 0.780714 13.3954C0.735196 13.4357 0.688645 13.4761 0.64313 13.5174C0.582096 10.6012 0.552096 9.05066 0.552096 7.49985C0.552096 5.94904 0.582183 4.39957 0.643217 1.48439ZM1.09424 13.8546C5.59016 13.9167 7.9508 13.9477 10.3104 13.9477C12.6741 13.9477 15.038 13.9167 19.5482 13.8546C16.3672 10.9095 16.03 10.6115 12.5262 7.85765L11.2186 9.0028C10.6983 9.45798 9.92241 9.45798 9.40206 9.0028L8.09759 7.86178C4.62283 10.7168 4.2587 11.0407 1.1502 13.8047C1.13158 13.8212 1.11286 13.838 1.09424 13.8546ZM8.27766 7.28573C8.28076 7.2878 8.28283 7.28987 8.2849 7.29194L9.76521 8.58709C10.0776 8.86018 10.5431 8.86018 10.8555 8.58709L12.3359 7.29194C12.3379 7.28987 12.341 7.2878 12.3441 7.28573L12.3772 7.25884C12.399 7.24022 12.4321 7.21332 12.4765 7.17712C12.5645 7.1047 12.6969 6.99712 12.8696 6.85435C13.2162 6.56781 13.7283 6.1416 14.3914 5.58195C15.6172 4.54643 17.3603 3.0537 19.5244 1.14509C15.0296 1.08302 12.67 1.05199 10.3104 1.05199C7.94975 1.05199 5.58913 1.08302 1.09136 1.14509C2.89858 2.75368 4.61591 4.22473 5.9191 5.32322C6.63392 5.9263 7.22358 6.41665 7.6353 6.757C7.84012 6.92665 8.0015 7.05906 8.11116 7.14907C8.16495 7.19355 8.20736 7.22872 8.23529 7.25148L8.27766 7.28573ZM19.9775 1.48042C17.77 3.4304 15.9927 4.952 14.7472 6.00414C14.0821 6.56483 13.569 6.99207 13.2214 7.27964C13.119 7.36343 13.0311 7.43584 12.9586 7.49584C16.4106 10.2103 16.7634 10.5249 19.9786 13.501C20.0386 10.5951 20.0686 9.04771 20.0686 7.50008C20.0686 5.94941 20.0385 4.39745 19.9775 1.48042ZM0.375242 0.603188C10.3093 0.465604 10.3114 0.465604 20.2452 0.603188C20.3932 0.605257 20.5142 0.725256 20.5173 0.873176C20.6549 7.49801 20.6549 7.50199 20.5173 14.1268C20.5142 14.2748 20.3932 14.3947 20.2452 14.3968C10.3112 14.5344 10.3091 14.5344 0.375242 14.3968C0.227314 14.3947 0.106286 14.2747 0.103188 14.1268C-0.034396 7.50199 -0.034396 7.49801 0.103188 0.873176C0.106291 0.725248 0.227324 0.605254 0.375242 0.603188Z" fill="#1A2E25"/></svg>
                <p>{contactEmail}</p>
              </div>
            }
        </div>
        </div>
        <div className="flex flex-col firmDetailsContInner">
          <p className="font-semibold bodyLarger mb-1">{textData.Pages.FounderPages.ServiceProviderFirmPages.FirmDetails.Contact.eng}</p>
          {Array.isArray(contact) 
            ? contact.map((item, index) => (
                <p key={index} className="font-semibold mb-1">{item}</p>
              )) 
            : <p className="font-semibold mb-1">{contact}</p>
          }
          <div className="flex flex-col">
          {Array.isArray(contactEmail) 
            ? contactEmail.map((item, index) => (
              <div className="flex mb-1">
                <svg xmlns="http://www.w3.org/2000/svg" className="mr-2" width="21" height="15" viewBox="0 0 21 15" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M0.643217 1.48439C2.48353 3.12505 4.23794 4.62706 5.56312 5.74541C6.28 6.34952 6.87069 6.84091 7.28344 7.18229C7.43758 7.31057 7.56688 7.41712 7.66826 7.49987C4.25759 10.3022 3.87174 10.6457 0.780714 13.3954C0.735196 13.4357 0.688645 13.4761 0.64313 13.5174C0.582096 10.6012 0.552096 9.05066 0.552096 7.49985C0.552096 5.94904 0.582183 4.39957 0.643217 1.48439ZM1.09424 13.8546C5.59016 13.9167 7.9508 13.9477 10.3104 13.9477C12.6741 13.9477 15.038 13.9167 19.5482 13.8546C16.3672 10.9095 16.03 10.6115 12.5262 7.85765L11.2186 9.0028C10.6983 9.45798 9.92241 9.45798 9.40206 9.0028L8.09759 7.86178C4.62283 10.7168 4.2587 11.0407 1.1502 13.8047C1.13158 13.8212 1.11286 13.838 1.09424 13.8546ZM8.27766 7.28573C8.28076 7.2878 8.28283 7.28987 8.2849 7.29194L9.76521 8.58709C10.0776 8.86018 10.5431 8.86018 10.8555 8.58709L12.3359 7.29194C12.3379 7.28987 12.341 7.2878 12.3441 7.28573L12.3772 7.25884C12.399 7.24022 12.4321 7.21332 12.4765 7.17712C12.5645 7.1047 12.6969 6.99712 12.8696 6.85435C13.2162 6.56781 13.7283 6.1416 14.3914 5.58195C15.6172 4.54643 17.3603 3.0537 19.5244 1.14509C15.0296 1.08302 12.67 1.05199 10.3104 1.05199C7.94975 1.05199 5.58913 1.08302 1.09136 1.14509C2.89858 2.75368 4.61591 4.22473 5.9191 5.32322C6.63392 5.9263 7.22358 6.41665 7.6353 6.757C7.84012 6.92665 8.0015 7.05906 8.11116 7.14907C8.16495 7.19355 8.20736 7.22872 8.23529 7.25148L8.27766 7.28573ZM19.9775 1.48042C17.77 3.4304 15.9927 4.952 14.7472 6.00414C14.0821 6.56483 13.569 6.99207 13.2214 7.27964C13.119 7.36343 13.0311 7.43584 12.9586 7.49584C16.4106 10.2103 16.7634 10.5249 19.9786 13.501C20.0386 10.5951 20.0686 9.04771 20.0686 7.50008C20.0686 5.94941 20.0385 4.39745 19.9775 1.48042ZM0.375242 0.603188C10.3093 0.465604 10.3114 0.465604 20.2452 0.603188C20.3932 0.605257 20.5142 0.725256 20.5173 0.873176C20.6549 7.49801 20.6549 7.50199 20.5173 14.1268C20.5142 14.2748 20.3932 14.3947 20.2452 14.3968C10.3112 14.5344 10.3091 14.5344 0.375242 14.3968C0.227314 14.3947 0.106286 14.2747 0.103188 14.1268C-0.034396 7.50199 -0.034396 7.49801 0.103188 0.873176C0.106291 0.725248 0.227324 0.605254 0.375242 0.603188Z" fill="#1A2E25"/></svg>
                <p key={index}>{item}</p>
              </div>
              )) 
            : 
            <div className="flex">
              <svg xmlns="http://www.w3.org/2000/svg" className="mr-2" width="21" height="15" viewBox="0 0 21 15" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M0.643217 1.48439C2.48353 3.12505 4.23794 4.62706 5.56312 5.74541C6.28 6.34952 6.87069 6.84091 7.28344 7.18229C7.43758 7.31057 7.56688 7.41712 7.66826 7.49987C4.25759 10.3022 3.87174 10.6457 0.780714 13.3954C0.735196 13.4357 0.688645 13.4761 0.64313 13.5174C0.582096 10.6012 0.552096 9.05066 0.552096 7.49985C0.552096 5.94904 0.582183 4.39957 0.643217 1.48439ZM1.09424 13.8546C5.59016 13.9167 7.9508 13.9477 10.3104 13.9477C12.6741 13.9477 15.038 13.9167 19.5482 13.8546C16.3672 10.9095 16.03 10.6115 12.5262 7.85765L11.2186 9.0028C10.6983 9.45798 9.92241 9.45798 9.40206 9.0028L8.09759 7.86178C4.62283 10.7168 4.2587 11.0407 1.1502 13.8047C1.13158 13.8212 1.11286 13.838 1.09424 13.8546ZM8.27766 7.28573C8.28076 7.2878 8.28283 7.28987 8.2849 7.29194L9.76521 8.58709C10.0776 8.86018 10.5431 8.86018 10.8555 8.58709L12.3359 7.29194C12.3379 7.28987 12.341 7.2878 12.3441 7.28573L12.3772 7.25884C12.399 7.24022 12.4321 7.21332 12.4765 7.17712C12.5645 7.1047 12.6969 6.99712 12.8696 6.85435C13.2162 6.56781 13.7283 6.1416 14.3914 5.58195C15.6172 4.54643 17.3603 3.0537 19.5244 1.14509C15.0296 1.08302 12.67 1.05199 10.3104 1.05199C7.94975 1.05199 5.58913 1.08302 1.09136 1.14509C2.89858 2.75368 4.61591 4.22473 5.9191 5.32322C6.63392 5.9263 7.22358 6.41665 7.6353 6.757C7.84012 6.92665 8.0015 7.05906 8.11116 7.14907C8.16495 7.19355 8.20736 7.22872 8.23529 7.25148L8.27766 7.28573ZM19.9775 1.48042C17.77 3.4304 15.9927 4.952 14.7472 6.00414C14.0821 6.56483 13.569 6.99207 13.2214 7.27964C13.119 7.36343 13.0311 7.43584 12.9586 7.49584C16.4106 10.2103 16.7634 10.5249 19.9786 13.501C20.0386 10.5951 20.0686 9.04771 20.0686 7.50008C20.0686 5.94941 20.0385 4.39745 19.9775 1.48042ZM0.375242 0.603188C10.3093 0.465604 10.3114 0.465604 20.2452 0.603188C20.3932 0.605257 20.5142 0.725256 20.5173 0.873176C20.6549 7.49801 20.6549 7.50199 20.5173 14.1268C20.5142 14.2748 20.3932 14.3947 20.2452 14.3968C10.3112 14.5344 10.3091 14.5344 0.375242 14.3968C0.227314 14.3947 0.106286 14.2747 0.103188 14.1268C-0.034396 7.50199 -0.034396 7.49801 0.103188 0.873176C0.106291 0.725248 0.227324 0.605254 0.375242 0.603188Z" fill="#1A2E25"/></svg>
              <p>{contactEmail}</p>
            </div>
          }
          </div>
        </div>
      </div>
      {/* <div className="flex justify-center mt-5">
        <Button 
          buttonType={"danger"} 
          buttonText={"Delete Firm"} 
          actionHandler={() => {setIsModalOpen(true)}}
        /> 
      </div> */}
      <Modal 
        isOpen={isModalOpen}
        closeModal={() => setIsModalOpen(false)}
        modalHeader="Confirm Service Provider Firm Deletion"
        contentLabel="Confirm if you would like to delete this firm"
        primaryButton={{ buttonText: "Confirm", buttonCallback: () => handleDelete() }}
        modalHTML={
          <div className="text-text">
            <p>Are you sure you want to delete the following Service Provider Firm:</p>
            <p className="font-bold my-5">{name}</p>
          </div>
        }
      />
    </div>
  )
}

export default FirmDetails;